// Client-side mirror of packages/db/src/email-preferences.ts (spec 16). Keep
// the category/kind lists in step with the server copy.

export type EmailCategory = "account" | "activity" | "digest" | "product";
export type OptionalEmailCategory = Exclude<EmailCategory, "account">;

export interface EmailPreferences {
  activity: boolean;
  digest: boolean;
  product: boolean;
}

export type EmailPreferencesPatch = Partial<EmailPreferences>;

export interface EmailPreferencesResponse {
  preferences: EmailPreferences;
}

export type LifecycleEmailKind =
  | "import_summary"
  | "unlock_batch"
  | "monthly_digest"
  | "winback"
  | "pro_started"
  | "pro_ending"
  | "payment_failed"
  | "strava_reconnect";

export const LIFECYCLE_EMAIL_KINDS: readonly LifecycleEmailKind[] = [
  "import_summary",
  "unlock_batch",
  "monthly_digest",
  "winback",
  "pro_started",
  "pro_ending",
  "payment_failed",
  "strava_reconnect",
];

export const EMAIL_KIND_LABELS: Record<string, string> = {
  welcome: "Welcome",
  "email-verification": "Email verification",
  "email-change": "Email change",
  "password-reset": "Password reset",
  "feedback-reply": "Feedback reply",
  import_summary: "Import summary",
  unlock_batch: "Unlock batch",
  monthly_digest: "Monthly digest",
  winback: "Win-back",
  pro_started: "Pro started",
  pro_ending: "Pro ending",
  payment_failed: "Payment failed",
  strava_reconnect: "Strava reconnect",
};

export const EMAIL_CATEGORY_COPY: Record<OptionalEmailCategory, { title: string; hint: string }> = {
  activity: {
    title: "Activity",
    hint: "Import summaries and a daily note when you unlock something.",
  },
  digest: {
    title: "Monthly digest",
    hint: "Your month outdoors in numbers, on the 1st.",
  },
  product: {
    title: "Product news",
    hint: "Occasional announcements about Atlas itself.",
  },
};

// ─── Email capture / change (GET + POST /account/email) ──────────────────────

export interface EmailStatusPending {
  /** The address the confirmation link went to. */
  email: string;
  sentAt: string;
  /** ISO time after which another send is allowed (60 s cooldown). */
  resendAvailableAt: string;
}

export interface EmailStatus {
  email: string;
  verified: boolean;
  /** True for `@athlete.atlas.internal` / `@atlas.internal` addresses (no real inbox). */
  placeholder: boolean;
  /** True when an account password is required to change the address. */
  requiresPassword: boolean;
  pending: EmailStatusPending | null;
}

export interface EmailChangeRequest {
  email: string;
  /** Required when the account has a password, unless resending to the pending address. */
  password?: string;
}

export interface EmailChangeResponse {
  ok: true;
  pending: EmailStatusPending;
}
